import React, { useEffect, useState } from 'react';
import { Table, Button, Form, Modal, Badge, Pagination, Spinner } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import { adminAPI } from '../services/api';
import { toast } from 'react-toastify';

const emptyForm = {
  name: '',
  email: '',
  phone: '',
  package_id: '',
  status: 'active'
};

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getUsers({ page, search });
      setUsers(response.data.data || []);
      setTotalPages(response.data.pagination?.pages || 1);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const resetModal = () => {
    setEditingUser(null);
    setFormData(emptyForm);
    setShowModal(false);
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      package_id: user.Licenses?.[0]?.package_id || '',
      status: user.status || 'active'
    });
    setShowModal(true);
  };

  const handleDelete = async (user) => {
    const confirmed = window.confirm(
      `Disable this end user?\n\n${user.name || ''}\n${user.email || ''}\n\nThis will not permanently delete license history.`
    );

    if (!confirmed) return;

    try {
      await adminAPI.deleteUser(user.id);
      toast.success('User disabled successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to disable user');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingUser) {
        await adminAPI.updateUser(editingUser.id, {
          name: formData.name,
          phone: formData.phone,
          status: formData.status,
          package_id: formData.package_id
        });
        toast.success('User updated successfully');
      } else {
        await adminAPI.createUser({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          package_id: formData.package_id
        });
        toast.success('User created successfully');
      }

      resetModal();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save user');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: 'success',
      disabled: 'secondary',
      pending: 'warning',
      expired: 'danger'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  if (loading) return <Spinner animation="border" />;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>End Users</h3>
        <Button variant="primary" onClick={openCreateModal}>
          <FaPlus /> Add User
        </Button>
      </div>

      <Form.Control
        type="text"
        placeholder="Search users..."
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(1);
        }}
        className="mb-3"
      />

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Phone</th>
            <th>Status</th>
            <th>License</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.length === 0 ? (
            <tr>
              <td colSpan="6" className="text-center">No users found</td>
            </tr>
          ) : (
            users.map((user) => (
              <tr key={user.id}>
                <td>{user.name}</td>
                <td>{user.email}</td>
                <td>{user.phone}</td>
                <td>{getStatusBadge(user.status)}</td>
                <td>{user.Licenses?.[0]?.license_key || 'N/A'}</td>
                <td className="table-actions">
                  <Button
                    variant="info"
                    size="sm"
                    className="me-2"
                    onClick={() => handleEdit(user)}
                    title="Edit user"
                  >
                    <FaEdit />
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDelete(user)}
                    title="Disable user"
                  >
                    <FaTrash />
                  </Button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      <Pagination>
        {[...Array(totalPages)].map((_, i) => (
          <Pagination.Item
            key={i + 1}
            active={i + 1 === page}
            onClick={() => setPage(i + 1)}
          >
            {i + 1}
          </Pagination.Item>
        ))}
      </Pagination>

      <Modal show={showModal} onHide={resetModal}>
        <Modal.Header closeButton>
          <Modal.Title>{editingUser ? 'Edit User' : 'Create New User'}</Modal.Title>
        </Modal.Header>

        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                required
                disabled={!!editingUser}
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Phone</Form.Label>
              <Form.Control
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Package</Form.Label>
              <Form.Select
                required={!editingUser}
                value={formData.package_id}
                onChange={(e) => setFormData({ ...formData, package_id: e.target.value })}
              >
                <option value="">Select Package</option>
                <option value="1">Trial</option>
                <option value="2">6 Months</option>
                <option value="3">1 Year</option>
                <option value="4">3 Years</option>
              </Form.Select>
              {editingUser && (
                <Form.Text className="text-muted">
                  Changing package will update this user's active license validity and reset limit.
                </Form.Text>
              )}
            </Form.Group>

            {editingUser && (
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <option value="active">Active</option>
                  <option value="disabled">Disabled</option>
                  <option value="pending">Pending</option>

                </Form.Select>
              </Form.Group>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={resetModal}>Cancel</Button>
            <Button variant="primary" type="submit">
              {editingUser ? 'Update User' : 'Create User'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default Users;
